import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, FormProvider } from "react-hook-form";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getSelectBanks, registerBank } from "@/Api/bankAccounts";
import { TableDataProps } from "@/pages/BankAccounts";
import { getSelectUnities } from "@/Api/unities";

export const BANK_ACCOUNT_TYPES = {
  CHECKING: "Corrente",
  SAVINGS: "Poupança",
} as const;

const NEW_BANK_OPTION = "__new_bank__";

export const BANK_ACCOUNT_TYPE_OPTIONS = [
  { id: 1, name: BANK_ACCOUNT_TYPES.CHECKING },
  { id: 2, name: BANK_ACCOUNT_TYPES.SAVINGS },
];

const formSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  surname: z.string().min(1, { message: "Sobrenome é obrigatório" }),
  bank_account_type_id: z.union([
    z.string().min(1, { message: "Tipo de conta é obrigatório" }),
    z.number().min(1, { message: "Tipo de conta é obrigatório" }),
  ]),
  bank_id: z.union([
    z.string().min(1, { message: "Banco é obrigatório" }),
    z.number().min(1, { message: "Banco é obrigatório" }),
  ]),
  agency: z.union([
    z.string().min(1, { message: "Agência é obrigatória" }),
    z.number().min(1, { message: "Agência é obrigatória" }),
  ]),
  account_number: z.union([
    z.string().min(1, { message: "Conta é obrigatória" }),
    z.number().min(1, { message: "Conta é obrigatória" }),
  ]),
  unity_id: z.union([
    z.string().min(1, { message: "Unidade é obrigatória" }),
    z.number().min(1, { message: "Unidade é obrigatória" }),
  ]),
});

type FormProps = {
  title: string;
  loading: boolean;
  onSubmit: (values: z.infer<typeof formSchema>) => void;
  buttonText: string;
  initialValues?: TableDataProps;
};

export function Form({
  title,
  loading,
  onSubmit,
  buttonText,
  initialValues,
}: FormProps) {
  const [banks, setBanks] = useState<{ name: string }[]>([]);
  const [unities, setUnities] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [loadingUnities, setLoadingUnities] = useState(false);
  const [addingBank, setAddingBank] = useState(false);
  const [newBankName, setNewBankName] = useState("");
  const [savingBank, setSavingBank] = useState(false);

  const methods = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues
      ? {
          name: initialValues.name,
          surname: initialValues.surname,
          bank_account_type_id: String(initialValues.bank_account_type_id),
          bank_id: String(initialValues.bank_id),
          agency: String(initialValues.agency),
          account_number: String(initialValues.account_number),
          unity_id: String(initialValues.unity_id),
        }
      : {
          name: "",
          surname: "",
          bank_account_type_id: "",
          bank_id: "",
          agency: "",
          account_number: "",
          unity_id: "",
        },
  });

  useEffect(() => {
    const fetchBanks = async () => {
      const { data } = await getSelectBanks();
      if (data) {
        setBanks(data || []);
      }
    };

    fetchBanks();
  }, []);

  const handleSaveNewBank = async () => {
    const name = newBankName.trim();
    if (!name) return;

    try {
      setSavingBank(true);
      const { data, ok } = await registerBank({ name });

      if (!ok) {
        toast.error(data?.message || "Não foi possível cadastrar o banco.");
        return;
      }

      const savedName = data?.response?.name ?? name;
      setBanks((prev) => [...prev, { name: savedName }]);
      methods.setValue("bank_id", savedName, { shouldValidate: true });
      methods.setValue("name", savedName);
      setAddingBank(false);
      setNewBankName("");
      toast.success("Banco cadastrado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao processar sua solicitação.");
    } finally {
      setSavingBank(false);
    }
  };

  useEffect(() => {
    const fetchUnities = async () => {
      try {
        setLoadingUnities(true);
        const { data } = await getSelectUnities();
        if (data?.response) {
          setUnities(data.response);
        }
      } catch (error) {
        console.error("Erro ao carregar unidades:", error);
      } finally {
        setLoadingUnities(false);
      }
    };

    fetchUnities();
  }, []);

  useEffect(() => {
    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        methods.setValue(key as any, String(initialValues[key]));
      });
    }
  }, [initialValues, methods]);

  return (
    <div className="space-y-4 py-2 pb-4">
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-8">
          <div className="grid gap-4 py-4">
            <FormField
              control={methods.control}
              name="bank_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Banco</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      if (value === NEW_BANK_OPTION) {
                        setAddingBank(true);
                        return;
                      }
                      setAddingBank(false);
                      field.onChange(value);
                      methods.setValue("name", value);
                    }}
                    value={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um banco" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {banks.map((bank) => (
                        <SelectItem key={bank.name} value={bank.name}>
                          {bank.name}
                        </SelectItem>
                      ))}
                      <SelectItem value={NEW_BANK_OPTION}>
                        + Outro banco...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {addingBank && (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Nome do banco (ex: CLARA)"
                        value={newBankName}
                        onChange={(e) => setNewBankName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveNewBank();
                          }
                        }}
                      />
                      <Button
                        type="button"
                        onClick={handleSaveNewBank}
                        disabled={savingBank || !newBankName.trim()}
                      >
                        {savingBank && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Salvar
                      </Button>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="surname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Apelido</FormLabel>
                  <FormControl>
                    <Input placeholder="Apelido da conta" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={methods.control}
              name="bank_account_type_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Conta</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo de conta" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BANK_ACCOUNT_TYPE_OPTIONS.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={methods.control}
                name="agency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agência</FormLabel>
                    <FormControl>
                      <Input placeholder="Agência" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="account_number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Conta</FormLabel>
                    <FormControl>
                      <Input placeholder="Número da conta" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={methods.control}
              name="unity_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Unidade</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={String(field.value)}
                  >
                    <FormControl>
                      <SelectTrigger disabled={loadingUnities}>
                        <SelectValue
                          placeholder={
                            loadingUnities
                              ? "Carregando..."
                              : "Selecione uma unidade"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {!loadingUnities &&
                        unities.map((unity) => (
                          <SelectItem key={unity.id} value={String(unity.id)}>
                            {unity.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end pt-4">
              <Button
                className="bg-primary hover:bg-primary/80"
                type="submit"
                disabled={loading}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {buttonText}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
